import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DollarSign, BarChart3, Users as UsersIcon, TrendingUp, X } from "lucide-react";
import api from "@/lib/api";

export const AnalyticsDashboard = ({ analytics: initialAnalytics }) => {
  const [analytics, setAnalytics] = useState(initialAnalytics);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchAnalytics = async (start, end) => {
    try {
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      const res = await api.get("/analytics", { params });
      setAnalytics(res.data);
    } catch { /* ignore */ }
  };

  const presets = [
    { label: "7D", days: 7 },
    { label: "30D", days: 30 },
    { label: "90D", days: 90 },
    { label: "1Y", days: 365 },
  ];

  return (
    <div>
      {/* Date Range Filter */}
      <div className="flex flex-wrap items-end gap-4 mb-8" data-testid="analytics-date-filter">
        <div>
          <Label className="font-manrope text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">From</Label>
          <Input data-testid="analytics-start-date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="font-manrope text-sm w-40" />
        </div>
        <div>
          <Label className="font-manrope text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">To</Label>
          <Input data-testid="analytics-end-date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="font-manrope text-sm w-40" />
        </div>
        <Button data-testid="analytics-apply-filter" onClick={() => fetchAnalytics(startDate, endDate)} className="bg-sandy text-midnight hover:bg-sandy/90 font-manrope text-xs rounded-full h-9 px-5">Apply</Button>
        {(startDate || endDate) && (
          <Button data-testid="analytics-clear-filter" variant="outline" onClick={() => { setStartDate(""); setEndDate(""); fetchAnalytics("", ""); }} className="font-manrope text-xs rounded-full h-9">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          {presets.map(p => (
            <Button key={p.label} data-testid={`analytics-preset-${p.label}`} variant="outline" size="sm" onClick={() => {
              const end = new Date().toISOString().slice(0, 10);
              const start = new Date(Date.now() - p.days * 86400000).toISOString().slice(0, 10);
              setStartDate(start); setEndDate(end); fetchAnalytics(start, end);
            }} className="font-manrope text-[10px] h-7 px-3">{p.label}</Button>
          ))}
        </div>
      </div>

      {analytics && analytics.net_revenue !== undefined ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpi-grid">
            <div className="p-5 bg-card border border-border" data-testid="kpi-net-revenue">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-sandy" />
                <span className="font-manrope text-[10px] uppercase tracking-widest text-muted-foreground">Net Revenue</span>
              </div>
              <p className="font-cormorant text-3xl text-foreground">${(analytics.net_revenue || 0).toLocaleString()}</p>
              <p className="font-manrope text-[10px] text-muted-foreground mt-1">After ${(analytics.total_refunds || 0).toLocaleString()} refunds</p>
            </div>
            <div className="p-5 bg-card border border-border" data-testid="kpi-total-bookings">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-sandy" />
                <span className="font-manrope text-[10px] uppercase tracking-widest text-muted-foreground">Bookings</span>
              </div>
              <p className="font-cormorant text-3xl text-foreground">{analytics.total_bookings || 0}</p>
              <p className="font-manrope text-[10px] text-muted-foreground mt-1">{analytics.cancelled_bookings || 0} cancelled</p>
            </div>
            <div className="p-5 bg-card border border-border" data-testid="kpi-total-guests">
              <div className="flex items-center gap-2 mb-2">
                <UsersIcon className="w-4 h-4 text-sandy" />
                <span className="font-manrope text-[10px] uppercase tracking-widest text-muted-foreground">Total Guests</span>
              </div>
              <p className="font-cormorant text-3xl text-foreground">{analytics.total_guests || 0}</p>
              <p className="font-manrope text-[10px] text-muted-foreground mt-1">Avg ${analytics.avg_order_value || 0}/order</p>
            </div>
            <div className="p-5 bg-card border border-border" data-testid="kpi-engagement">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-sandy" />
                <span className="font-manrope text-[10px] uppercase tracking-widest text-muted-foreground">Engagement</span>
              </div>
              <p className="font-cormorant text-3xl text-foreground">{analytics.total_leads || 0}</p>
              <p className="font-manrope text-[10px] text-muted-foreground mt-1">{analytics.total_referrals || 0} referrals &middot; {analytics.total_reviews || 0} reviews</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="p-6 bg-card border border-border" data-testid="analytics-revenue-by-tour">
              <h3 className="font-cormorant text-lg font-light mb-4">Revenue by Tour</h3>
              <div className="space-y-3">
                {(analytics.revenue_by_tour || []).map((t) => {
                  const maxRev = (analytics.revenue_by_tour || [])[0]?.revenue || 1;
                  return (
                    <div key={t.tour_id || t.title}>
                      <div className="flex justify-between font-manrope text-xs mb-1">
                        <span className="text-foreground truncate mr-2">{t.title}</span>
                        <span className="text-muted-foreground shrink-0">${(t.revenue || 0).toLocaleString()} ({t.bookings})</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-sandy rounded-full" style={{ width: `${(t.revenue / maxRev) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-6 bg-card border border-border" data-testid="analytics-revenue-by-category">
              <h3 className="font-cormorant text-lg font-light mb-4">Revenue by Category</h3>
              <div className="space-y-4">
                {(analytics.revenue_by_category || []).map((c) => {
                  const totalCatRev = (analytics.revenue_by_category || []).reduce((s, x) => s + x.revenue, 0) || 1;
                  const pct = Math.round((c.revenue / totalCatRev) * 100);
                  const colors = { Land: "bg-canopy", Sea: "bg-blue-500", Adventure: "bg-sandy", "Epic Combos": "bg-purple-500", "Kids Package": "bg-pink-500" };
                  return (
                    <div key={c.category}>
                      <div className="flex justify-between font-manrope text-xs mb-1">
                        <span className="text-foreground">{c.category}</span>
                        <span className="text-muted-foreground">${(c.revenue || 0).toLocaleString()} ({pct}%)</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div className={`h-full ${colors[c.category] || "bg-sandy"} rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-6 bg-card border border-border" data-testid="analytics-monthly-revenue">
              <h3 className="font-cormorant text-lg font-light mb-4">Monthly Revenue</h3>
              {(analytics.revenue_by_month || []).length > 0 ? (
                <div className="space-y-2">
                  {(analytics.revenue_by_month || []).map((m) => {
                    const maxMon = Math.max(...(analytics.revenue_by_month || []).map(x => x.revenue)) || 1;
                    return (
                      <div key={m.month} className="flex items-center gap-3">
                        <span className="font-manrope text-xs text-muted-foreground w-16 shrink-0">{m.month}</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-sandy rounded-full" style={{ width: `${(m.revenue / maxMon) * 100}%` }} />
                        </div>
                        <span className="font-manrope text-xs text-foreground w-20 text-right">${(m.revenue || 0).toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="font-manrope text-xs text-muted-foreground">No monthly data yet</p>
              )}
            </div>

            <div className="p-6 bg-card border border-border" data-testid="analytics-breakdown">
              <h3 className="font-cormorant text-lg font-light mb-4">Discount Usage</h3>
              {(analytics.discount_breakdown || []).length > 0 ? (
                <div className="space-y-2 mb-6">
                  {(analytics.discount_breakdown || []).map((d) => (
                    <div key={d.type} className="flex justify-between items-center font-manrope text-xs">
                      <Badge variant="outline" className="text-[10px] capitalize">{d.type}</Badge>
                      <span className="text-foreground">{d.count} bookings</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-manrope text-xs text-muted-foreground mb-6">No discounts used</p>
              )}
              <h3 className="font-cormorant text-lg font-light mb-4">Payment Methods</h3>
              <div className="space-y-2">
                {Object.entries(analytics.payment_methods || {}).map(([method, count]) => (
                  <div key={method} className="flex justify-between items-center font-manrope text-xs">
                    <span className="text-muted-foreground capitalize">{method.replace("_", " ")}</span>
                    <span className="text-foreground">{count} bookings</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-16">
          <BarChart3 className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
          <p className="font-manrope text-sm text-muted-foreground">Loading analytics...</p>
        </div>
      )}
    </div>
  );
};
